import { useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Transaction } from '@/types/transaction';
import { useTransaction } from './useTransaction';
import { useTransactionAttachments } from './useTransactionAttachments';
import { fromDateString } from '@/lib/utils/dateUtils';
import {
  isPGRST200Error,
  normalizePaidFields,
  normalizePaidField,
  fetchCategoriesMap,
  fetchClientsMap,
  fetchSuppliersMap,
} from '@/lib/supabaseResilient';

interface UseTransactionsOptions {
  type?: 'income' | 'expense';
}

export function useTransactions(options?: UseTransactionsOptions) {
  const { type } = options || {};

  const { create, update, remove, togglePaid } = useTransaction();
  const {
    getTransactionAttachments,
    uploadAttachments,
    deleteAttachment,
    downloadAttachment,
    runDiagnostics,
  } = useTransactionAttachments();

  const { data: transactions, isLoading } = useQuery({
    queryKey: ['transactions', type],
    queryFn: async () => {
      const { data: { user }, error: userError } = await supabase.auth.getUser();

      if (userError) {
        throw userError;
      }

      if (!user) {
        return [];
      }

      // Tenta embedded categories; se PGRST200 (sem FK), faz fallback com join manual
      let data: any[] | null = null;
      let error: any = null;
      {
        let q: any = supabase
          .from('transactions')
          .select(`
          *,
          categories (
            id,
            name
          )
        `)
          .eq('user_id', user.id);
        if (type) q = q.eq('type', type);
        const res = await q.order('date', { ascending: false });
        data = res.data as any;
        error = res.error;
        if (error && isPGRST200Error(error)) {
          console.warn('[useTransactions] PGRST200 em categories embedded, fallback sem join', error.message);
          let q2: any = supabase.from('transactions').select('*').eq('user_id', user.id);
          if (type) q2 = q2.eq('type', type);
          const res2: any = await q2.order('date', { ascending: false });
          if (res2.error) {
            error = res2.error;
            data = null;
          } else {
            const normalized = normalizePaidFields((res2.data ?? []) as any[]);
            const catIds = normalized.map((r: any) => r.category_id).filter((v: any) => v != null);
            const catsMap = await fetchCategoriesMap(catIds, user.id);
            data = normalized.map((row: any) => ({
              ...row,
              categories: row.category_id ? (catsMap.get(Number(row.category_id)) ?? catsMap.get(row.category_id) ?? null) : null,
            }));
            error = null;
          }
        }
      }

      if (error) {
        throw error;
      }

      const normalizedData = normalizePaidFields((data ?? []) as any[]);
      // Don't use dbToApp - it converts is_paid to isPaid which breaks the UI
      // Keep database field names as-is since Transaction interface uses snake_case
      return normalizedData.map(transaction => {
        return {
          ...transaction,
          date: fromDateString(transaction.date),
          due_date: transaction.due_date ? fromDateString(transaction.due_date) : null,
          payment_date: transaction.payment_date ? fromDateString(transaction.payment_date) : null,
          category: (transaction as any).categories?.name || 'Sem Categoria',
          client: transaction.client,
          supplier: transaction.supplier,
        };
      }) as Transaction[];
    },
  });

  const getTransactionById = useCallback(async (id: string) => {

    const { data: { user }, error: userError } = await supabase.auth.getUser();

    if (userError || !user) {
      console.error("Erro ao obter usuário:", userError);
      throw new Error('Usuário não autenticado');
    }

    // Tenta embedded com categories/clients/suppliers; se PGRST200 faz fallback manual
    let data: any = null;
    let error: any = null;
    {
      const res = await supabase
        .from('transactions')
        .select(`
        *,
        categories (
          id,
          name
        ),
        clients:client_id (
          id,
          name
        ),
        suppliers:supplier_id (
          id,
          name
        )
      `)
        .eq('id', id)
        .eq('user_id', user.id)
        .single();
      data = res.data as any;
      error = res.error as any;
      if (error && isPGRST200Error(error)) {
        console.warn('[useTransactions.getById] PGRST200 embedded, fallback manual', error.message);
        const res2: any = await supabase.from('transactions').select('*').eq('id', id).eq('user_id', user.id).single();
        if (res2.error) {
          error = res2.error;
          data = null;
        } else {
          let row: any = normalizePaidField({ ...(res2.data as any) });
          // Enriquecimento manual paralelo
          const [catsMap, clientsMap, suppliersMap] = await Promise.all([
            row.category_id ? fetchCategoriesMap([row.category_id], user.id) : Promise.resolve(new Map()),
            row.client_id ? fetchClientsMap([row.client_id], user.id) : Promise.resolve(new Map()),
            row.supplier_id ? fetchSuppliersMap([row.supplier_id], user.id) : Promise.resolve(new Map()),
          ]);
          row.categories = row.category_id ? (catsMap.get(Number(row.category_id)) ?? catsMap.get(row.category_id) ?? null) : null;
          row.clients = row.client_id ? (clientsMap.get(row.client_id) ?? null) : null;
          row.suppliers = row.supplier_id ? (suppliersMap.get(row.supplier_id) ?? null) : null;
          data = row;
          error = null;
        }
      }
    }

    if (error) {
      console.error("Erro ao buscar transação por ID:", error);
      throw error;
    }

    const normalizedSingle: any = normalizePaidField({ ...(data as any) });
    console.log("Transação encontrada (FULL):", JSON.stringify(normalizedSingle, null, 2));

    // Don't use dbToApp - keep database field names as-is
    return {
      ...normalizedSingle,
      date: fromDateString(normalizedSingle.date),
      due_date: normalizedSingle.due_date ? fromDateString(normalizedSingle.due_date) : null,
      payment_date: normalizedSingle.payment_date ? fromDateString(normalizedSingle.payment_date) : null,
      category: normalizedSingle.categories?.name || 'Sem Categoria',
      client: normalizedSingle.clients?.name || normalizedSingle.client,
      supplier: normalizedSingle.suppliers?.name || normalizedSingle.supplier,
    } as Transaction;
  }, []);

  return {
    transactions,
    isLoading,
    createTransaction: create,
    editTransaction: update,
    deleteTransaction: remove,
    toggleTransactionPaid: togglePaid,
    getTransactionAttachments,
    getTransactionById,
    deleteAttachment,
    downloadAttachment,
    runDiagnostics,
  };
}
